import type { EVENT_COLOR } from '@repo/types/enums/eventColor';
import type { MeetingPublic } from '@repo/types/meetings';
import type { ComponentProps } from 'react';

import { normalizeEventTimeToDay } from '@repo/lib/calendar/normalizeEventTimeToDay';
import { cn } from '@repo/lib/cn';
import { VIEW_KEY } from '@repo/types/enums/viewKey';
import { differenceInMinutes, format, isPast } from 'date-fns';
import { useMemo } from 'react';

import { getCalendarEventColor, getBorderRadiusClasses, getEventColorClasses } from '@/utils/calendar';

import { EventChildren } from './EventChildren';

const formatTimeWithOptionalMinutes = (date: Date) => {
  return format(date, 'HH:mm');
};

interface EventWrapperProps extends ComponentProps<'button'> {
  event: MeetingPublic;
  eventColor: EVENT_COLOR;
  isFirstDay?: boolean;
  isLastDay?: boolean;
  isDragging?: boolean;
  currentTime?: Date;
}

function EventWrapper(eventWrapperProps: EventWrapperProps) {
  const { event, eventColor, isFirstDay = true, isLastDay = true, isDragging, onClick, className, children, currentTime, onMouseDown, onTouchStart, ...props } = eventWrapperProps;

  // Always use the currentTime (if provided) to determine if the event is in the past
  const displayEnd = currentTime ? new Date(new Date(currentTime).getTime() + (new Date(event.endDate).getTime() - new Date(event.startDate).getTime())) : new Date(event.endDate);

  const isEventInPast = isPast(displayEnd);
  const isEventCancelled = event.isCancelled;
  return (
    <button
      className={cn(
        'focus-visible:border-border-primary outline-focus-ring flex h-full w-full overflow-hidden px-1 text-left font-medium outline-0 backdrop-blur-md transition outline-none select-none focus-visible:outline-3 data-cancelled-event:line-through data-dragging:cursor-grabbing data-dragging:shadow-lg sm:px-2',
        getEventColorClasses(eventColor),
        getBorderRadiusClasses(isFirstDay, isLastDay),
        className
      )}
      data-dragging={isDragging || undefined}
      data-past-event={isEventInPast || undefined}
      data-cancelled-event={isEventCancelled || undefined}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      {...props}
    >
      {children}
    </button>
  );
}

interface EventItemProps extends ComponentProps<'button'> {
  /** `childIds` is present only on the merged parent calendar. */
  event: MeetingPublic & { childIds?: number[] };
  view: VIEW_KEY;
  isDragging?: boolean;
  showTime?: boolean;
  currentTime?: Date;
  isFirstDay?: boolean;
  isLastDay?: boolean;
}

export function EventItem(eventItemProps: EventItemProps) {
  const { event, view, isDragging, onClick, showTime, currentTime, isFirstDay = true, isLastDay = true, children, className, onMouseDown, onTouchStart } = eventItemProps;
  const eventColor = getCalendarEventColor(event);

  const displayStart = useMemo<Date>(() => {
    return currentTime || new Date(event.startDate);
  }, [currentTime, event.startDate]);

  const displayEnd = useMemo<Date>(() => {
    return currentTime ? new Date(new Date(currentTime).getTime() + (new Date(event.endDate).getTime() - new Date(event.startDate).getTime())) : new Date(event.endDate);
  }, [currentTime, event.startDate, event.endDate]);

  // Calculate event duration in minutes
  const durationMinutes = useMemo<number>(() => {
    return differenceInMinutes(displayEnd, displayStart);
  }, [displayStart, displayEnd]);

  const { adjustedEndDate, adjustedStartDate } = normalizeEventTimeToDay({
    startDate: displayStart.toISOString(),
    endDate: displayEnd.toISOString(),
  });

  const getEventTime = () => {
    if (event.allDay) return 'Cały dzień';

    // For short events (less than 45 minutes), only show start time
    if (durationMinutes < 45) {
      return formatTimeWithOptionalMinutes(displayStart);
    }

    // For longer events, show both start and end time
    if (!adjustedStartDate || !adjustedEndDate) return null;
    return `${formatTimeWithOptionalMinutes(new Date(adjustedStartDate))} - ${formatTimeWithOptionalMinutes(new Date(adjustedEndDate))}`;
  };

  if (view === VIEW_KEY.MONTH) {
    return (
      <EventWrapper
        event={event}
        eventColor={eventColor}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
        isDragging={isDragging}
        onClick={onClick}
        className={cn('mt-(--event-gap) h-(--event-height) items-center text-[10px] sm:text-xs', className)}
        currentTime={currentTime}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
      >
        {children || (
          <span className="truncate">
            {!event.allDay && <span className="truncate text-[11px] font-normal opacity-70">{formatTimeWithOptionalMinutes(displayStart)} </span>}
            <EventChildren childIds={event.childIds} className="me-1" />
            {event.name}
          </span>
        )}
      </EventWrapper>
    );
  }

  if (view === VIEW_KEY.WEEK || view === VIEW_KEY.DAY) {
    return (
      <EventWrapper
        event={event}
        eventColor={eventColor}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
        isDragging={isDragging}
        onClick={onClick}
        className={cn('py-1', durationMinutes < 45 ? 'items-center' : 'flex-col', view === VIEW_KEY.WEEK ? 'text-[10px] sm:text-xs' : 'text-xs', className)}
        currentTime={currentTime}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
      >
        {durationMinutes < 45 ? (
          <div className="truncate">
            <EventChildren childIds={event.childIds} className="me-1" />
            {event.name} {showTime && <span className={cn('opacity-70', view === VIEW_KEY.WEEK ? 'sm:text-[11px]' : 'text-[11px]')}>{formatTimeWithOptionalMinutes(displayStart)}</span>}
          </div>
        ) : (
          <>
            <div className="truncate font-medium">
              <EventChildren childIds={event.childIds} className="me-1" />
              {event.name}
            </div>
            {showTime && <div className="truncate text-[11px] font-normal opacity-70">{getEventTime()}</div>}
          </>
        )}
      </EventWrapper>
    );
  }

  // Agenda view - kept separate since it's significantly different
  return (
    <button
      className={cn(
        'outline-focus-ring focus-visible:otline-2 flex w-full flex-col gap-1 rounded p-2 text-left outline-0 transition data-cancelled-event:line-through data-cancelled-event:opacity-95 data-past-event:opacity-95',
        getEventColorClasses(eventColor),
        className
      )}
      data-past-event={isPast(new Date(event.endDate)) || undefined}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
    >
      <div className="text-sm font-medium">
        <EventChildren childIds={event.childIds} className="me-1" />
        {event.name}
      </div>
      <div className="space-y-1 text-xs opacity-70">
        {event.allDay ? (
          <span>Cały dzień</span>
        ) : (
          <span className="uppercase">
            {formatTimeWithOptionalMinutes(displayStart)} - {formatTimeWithOptionalMinutes(displayEnd)}
          </span>
        )}
        <p>{event.type === 'remote' ? 'Zdalnie' : 'Stacjonarnie'}</p>
      </div>
      {event.desc && <div className="my-1 truncate text-xs opacity-90">{event.desc}</div>}
    </button>
  );
}
